import {kc} from '../service/keycloak';

const extractFilenameFromHeaders = (response) => {
  const disposition = response.headers.get('Content-Disposition');
  if (!disposition) {
    return null;
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(disposition);
  if (asciiMatch) {
    return asciiMatch[1];
  }
  return null;
};

const extractFilenameFromUrl = (url) => {
  try {
    const {pathname} = new URL(url);
    const last = pathname.substring(pathname.lastIndexOf('/') + 1);
    return last || 'archivo';
  } catch (e) {
    return 'archivo';
  }
};

const rewriteToConfiguredHost = (originalUrl) => {
  try {
    const target = new URL(originalUrl);
    if (!target.pathname.startsWith('/api/')) {
      return originalUrl;
    }
    const apiUrl = new URL(process.env.REP_API_URL);
    target.protocol = apiUrl.protocol;
    target.host = apiUrl.host;
    return target.toString();
  } catch (e) {
    return originalUrl;
  }
};

const isAuthenticatedTarget = (finalUrl) => {
  try {
    const target = new URL(finalUrl, window.location.origin);
    if (target.origin === window.location.origin) {
      return true;
    }
    const apiUrl = new URL(process.env.REP_API_URL);
    return target.host === apiUrl.host;
  } catch (e) {
    return false;
  }
};

const openInNewTab = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const triggerBlobDownload = (blob, filename) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const buildAuthHeaders = async() => {
  try {
    if (kc.isTokenExpired()) {
      await kc.updateToken();
    }
  } catch (e) {
    // Token refresh failed — fall through; the request will surface the error.
  }
  return kc.token ? {'Authorization': `Bearer ${kc.token}`} : {};
};

const fetchBlob = async(url) => {
  const headers = await buildAuthHeaders();
  const response = await fetch(rewriteToConfiguredHost(url), {headers});
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  return {blob, response};
};

/**
 * Abre en una pestaña nueva un archivo que el backend sirve autenticado (p. ej. /api/files/<nombre>.html):
 * lo descarga con el token y abre el blob, porque una navegación del navegador no lleva la cabecera Authorization.
 */
/**
 * Descarga con el token un archivo servido por el backend y devuelve una URL de objeto (blob:) para mostrarlo
 * en la propia página (iframe, img) o abrirlo en una pestaña desde un clic del usuario. Quien la recibe debe
 * liberarla con URL.revokeObjectURL cuando ya no la use.
 */
export const fetchFileAsObjectUrl = async(url) => {
  const {blob} = await fetchBlob(url);
  return URL.createObjectURL(blob);
};

export const openFileInNewTab = async(url) => {
  if (!url) {
    return;
  }
  const finalUrl = rewriteToConfiguredHost(url);
  if (!isAuthenticatedTarget(finalUrl)) {
    openInNewTab(finalUrl);
    return;
  }
  try {
    const {blob} = await fetchBlob(url);
    window.open(URL.createObjectURL(blob), '_blank');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('openFileInNewTab failed:', error);
  }
};

export const downloadFile = async(url) => {
  if (!url) {
    return;
  }
  const finalUrl = rewriteToConfiguredHost(url);
  if (!isAuthenticatedTarget(finalUrl)) {
    openInNewTab(finalUrl);
    return;
  }
  try {
    const {blob, response} = await fetchBlob(url);
    const filename = extractFilenameFromHeaders(response) || extractFilenameFromUrl(url);
    triggerBlobDownload(blob, filename);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('downloadFile failed:', error);
  }
};
